import type { ValidationResult } from 'types';
import type { WorkInstructionEntity } from './work-instruction.entity';
import type { PendingMediaObjectReference } from '@redviking/argonaut-util/src/media.util';
import type { MediaUploadDialogPayload } from '../media/MediaUploadDialog.vue';

export const validateWorkInstruction = (entity: WorkInstructionEntity): ValidationResult => {
    if (!entity.name) {
        const errorMessage = 'Work Instruction name is required';
        return {
            errorMessage,
            status: 'error',
        };
    }

    if (!entity.version.config.steps.length) {
        const errorMessage = 'Work Instruction must have at least one step';
        return {
            errorMessage,
            status: 'error',
        };
    }

    const sortedSteps = entity.version.config.steps.sort((a, b) => a.stepNum - b.stepNum);

    let lastStepNum = -1;
    for (const step of sortedSteps) {
        if (!step.name) {
            const errorMessage = `Step ${step.stepNum + 1} must have a name`;
            return {
                errorMessage,
                status: 'error',
            };
        }

        if (sortedSteps.find(s => s.name === step.name && s.stepNum !== step.stepNum)) {
            const errorMessage = `Step names must be unique. Found duplicate step name: ${step.name}`;
            return {
                errorMessage,
                status: 'error',
            };
        }

        if (step.stepNum <= lastStepNum || step.stepNum - lastStepNum !== 1) {
            /**
             * NOTE: There is no direct way for a user to set the step number
             * Step order can only be modified by dragging and dropping steps in the UI
             * This error message is mostly for debugging purposes denoting there is a problem with the drag and drop logic
             */
            const errorMessage = `Step numbers must be unique and in ascending order. Found duplicate or out of order step number: ${step.stepNum}`;
            return {
                errorMessage,
                status: 'error',
            };
        } else {
            lastStepNum = step.stepNum;
        }

        if (!step.advanceConfig.conditions.length) {
            const errorMessage = `Step ${step.stepNum + 1} must have at least one complete condition`;
            return {
                errorMessage,
                status: 'error',
            };
        }

        for (const linkedProcessId of step.linkedProcessIds) {
            const stepsThatAlsoUseProcess = sortedSteps.filter(s => s.linkedProcessIds.includes(linkedProcessId) && s.stepNum !== step.stepNum);
            if (stepsThatAlsoUseProcess.length) {
                const errorMessage = `Step ${step.stepNum + 1} uses a process that is also used in the following steps: ${stepsThatAlsoUseProcess.map(i => i.name).join(', ')}. A process can only be linked to one step`;
                return {
                    errorMessage,
                    status: 'error',
                };
            }
        }
    }
    const referencedPendingMedia = entity.version.config.steps.reduce((acc, step) => {
        const linkedMedia = step.linkedMedia;
        if (linkedMedia && linkedMedia.type === 'pending' && !acc.find(p => p.id === linkedMedia.id)) {
            acc.push(linkedMedia);
        }
        return acc;
    }, [] as PendingMediaObjectReference[]);
    if (referencedPendingMedia.length) {
        return {
            status: 'modal',
            modalComponent: () => import(/* webpackChunkName: "work-instructions" */ '../media/MediaUploadDialog.vue').then(m => m.default),
            modalProps: {
                pendingMedia: referencedPendingMedia,
                updateEntity: (payload: MediaUploadDialogPayload) => {
                    payload.accessor.setPageEntity({
                        type: 'workInstruction',
                        entity: {
                            ...entity,
                            version: {
                                ...entity.version,
                                config: {
                                    ...entity.version.config,
                                    steps: entity.version.config.steps.map(step => {
                                        if (step.linkedMedia && step.linkedMedia.type === 'pending') {
                                            const savedMediaItem = payload.savedMedia.find(m => m.file.id === (step.linkedMedia as PendingMediaObjectReference)?.id);
                                            if (savedMediaItem) {
                                                return {
                                                    ...step,
                                                    linkedMedia: savedMediaItem,
                                                };
                                            }
                                        }
                                        return step;
                                    }),
                                },
                            },
                        },
                    });
                },
            },
        };
    }

    return {
        status: 'success',
    };
};
